import {
  Alert as MuiAlert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import * as admin from '@/api/admin';
import type { InvoiceKind, InvoiceStatus } from '@/api/invoices';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatCurrency } from '@/lib/formatters';
import { INVOICE_KIND_LABEL, INVOICE_STATUS_COLOR, INVOICE_STATUS_LABEL } from '@/lib/labels';

export const Route = createFileRoute('/_authenticated/admin/comprobantes/')({
  component: ComprobantesPage,
});

type Mode = 'recent' | 'problematic';

function formatDateOnly(d: string | null | undefined): string {
  if (!d) return '—';
  const parsed = new Date(`${d}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function kindLabel(kind: string): string {
  return INVOICE_KIND_LABEL[kind as InvoiceKind] ?? kind;
}

function statusChip(status: string): JSX.Element {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={INVOICE_STATUS_COLOR[status as InvoiceStatus] ?? 'default'}
      label={INVOICE_STATUS_LABEL[status as InvoiceStatus] ?? status}
    />
  );
}

function pvNumero(pv: number, number: number | null): string {
  return `${pv.toString().padStart(5, '0')}-${number ? number.toString().padStart(8, '0') : '—'}`;
}

function ComprobantesPage(): JSX.Element {
  const [mode, setMode] = useState<Mode>('recent');

  const recentQ = useQuery({
    queryKey: ['admin-invoices'],
    queryFn: () => admin.getAdminRecentInvoices(100),
    enabled: mode === 'recent',
  });
  const problemQ = useQuery({
    queryKey: ['admin-invoices-problematic'],
    queryFn: () => admin.getProblematicInvoices(100),
    enabled: mode === 'problematic',
  });

  const q = mode === 'recent' ? recentQ : problemQ;

  return (
    <Box>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLong color="primary" /> Comprobantes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vista de soporte cross-tenant. CUIT del receptor y CAE van <strong>enmascarados</strong> por política de
          retención.
        </Typography>
      </Stack>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => v && setMode(v as Mode)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="recent">Recientes</ToggleButton>
        <ToggleButton value="problematic">Rechazados / Fallidos</ToggleButton>
      </ToggleButtonGroup>

      {q.isLoading ? (
        <LoadingScreen message="Cargando comprobantes…" />
      ) : q.error ? (
        <MuiAlert severity="error">{toApiError(q.error).message}</MuiAlert>
      ) : mode === 'recent' ? (
        <RecentTable items={recentQ.data ?? []} />
      ) : (
        <ProblematicTable items={problemQ.data ?? []} />
      )}
    </Box>
  );
}

function RecentTable({ items }: { items: admin.AdminInvoiceView[] }): JSX.Element {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptLong fontSize="inherit" />}
        title="Sin comprobantes"
        description="No hay comprobantes recientes en la plataforma."
      />
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Tenant</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>PV-Número</TableCell>
            <TableCell>Receptor</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell>CAE</TableCell>
            <TableCell>Estado</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((inv, i) => (
            <TableRow key={`${inv.tenantId}-${inv.pointOfSale}-${inv.number ?? i}`} hover>
              <TableCell>
                <Typography variant="caption">{formatDateOnly(inv.invoiceDate)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>
                  {inv.tenantId.slice(0, 8)}…
                </Typography>
              </TableCell>
              <TableCell>{kindLabel(inv.kind)}</TableCell>
              <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{pvNumero(inv.pointOfSale, inv.number)}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)' }}>
                  {inv.receiverDocMasked || '—'}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(inv.total, inv.currency)}
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>
                  {inv.caeMasked ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>{statusChip(inv.status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ProblematicTable({ items }: { items: admin.AdminProblematicInvoice[] }): JSX.Element {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptLong fontSize="inherit" />}
        title="Nada para revisar"
        description="No hay comprobantes rechazados ni fallidos. 🎉"
      />
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Tenant</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>PV-Número</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell align="right">Intentos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((inv, i) => {
            const reason = [inv.code, inv.reason].filter(Boolean).join(': ');
            return (
              <TableRow key={`${inv.tenantId}-${inv.pointOfSale}-${inv.number ?? i}`} hover>
                <TableCell>
                  <Typography variant="caption">{formatDateOnly(inv.invoiceDate)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'var(--font-mono)' }}>
                    {inv.tenantId.slice(0, 8)}…
                  </Typography>
                </TableCell>
                <TableCell>{kindLabel(inv.kind)}</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{pvNumero(inv.pointOfSale, inv.number)}</TableCell>
                <TableCell>{statusChip(inv.status)}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Tooltip title={reason}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'error.main',
                      }}
                    >
                      {reason || '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {inv.attemptCount}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
